/**
 * DieticianDomain - Dietician Domain Service
 *
 * Handles all dietician-related operations including registration, profile management,
 * schedule configuration, and photo uploads. Extends BaseDomain to leverage shared
 * infrastructure for API calls, retry logic, and error handling.
 *
 * Features:
 * - Dietician account registration
 * - Profile creation and updates
 * - Schedule configuration with Calendly integration
 * - Profile photo upload
 * - Profile retrieval with retry logic
 * - Integration with StorageManager for token management
 *
 * @module services/domains/DieticianDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager } from '../shared';
import type {
  DieticianRegistrationApiRequest,
  DieticianProfileApiRequest,
  DieticianProfileApiResponse,
  DieticianScheduleApiRequest,
  ApiResponse
} from '@/types/dieticianRegistration';
import type {
  DietitianProfile,
  GetDietitiansRequest,
  DietitianApiResponseProfile,
  HealthCondition,
  AvailabilityStatus
} from '@/types/referral';
import type { LanguageCode } from '@/types/languageCodes';

/**
 * Dietician-specific error class
 */
export class DieticianApiError extends BaseApiError {
  constructor(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'DieticianApiError';
  }

  /**
   * Create error from HTTP response
   */
  static fromHttpStatus(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): DieticianApiError {
    return new DieticianApiError(message, status, fieldErrors, code);
  }
}

/**
 * Registration response interface
 */
export interface RegistrationResponse {
  userId: string;
  status: string;
  message: string;
}

/**
 * Schedule configuration response interface
 */
export interface ScheduleResponse {
  scheduleId: string;
  calendlyIntegrated: boolean;
}

/**
 * Search endpoints for dietitian operations
 */
const SEARCH_ENDPOINTS = {
  DIETITIANS: '/dieticians/',
  DIETITIAN_BY_ID: (id: string) => `/dieticians/${id}`,
} as const;

/**
 * Dietician domain service class
 */
export class DieticianDomain extends BaseDomain<DieticianApiError> {
  constructor() {
    const apiClient = new ApiClient<DieticianApiError>(
      (message, status, fieldErrors, code) => new DieticianApiError(message, status, fieldErrors, code),
      {
        // Use localhost:8000 in test environment to work with MSW
        baseUrl: import.meta.env.MODE === 'test' ? 'http://localhost:8000/api' : undefined
      }
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Step 1: Create dietician account using /api/auth/signup
   *
   * @param data - Dietician registration data
   * @returns Promise resolving to registration response
   */
  async registerDietician(data: DieticianRegistrationApiRequest): Promise<ApiResponse<RegistrationResponse>> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.post<{
          user_id: string;
          status: string;
          message: string;
        }>('/auth/signup', data);

        return {
          success: true,
          data: {
            userId: result.user_id,
            status: result.status,
            message: result.message
          }
        };
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'registerDietician',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'registerDietician',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'registerDietician',
          error instanceof Error ? error.message : 'Registration failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Step 2: Create/update dietician profile using /api/users/me/dietician-profile
   *
   * @param data - Dietician profile data
   * @returns Promise resolving to profile response
   */
  async updateProfile(data: DieticianProfileApiRequest): Promise<ApiResponse<DieticianProfileApiResponse>> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.put<DieticianProfileApiResponse>('/users/me/dietician-profile', data);

        return {
          success: true,
          data: result
        };
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'updateProfile',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'updateProfile',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'updateProfile',
          error instanceof Error ? error.message : 'Profile update failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Get dietician profile using /api/users/me/dietician-profile
   *
   * @returns Promise resolving to profile response
   */
  async getProfile(): Promise<ApiResponse<DieticianProfileApiResponse>> {
    return this.executeWithDeduplication('dietician-profile', async () => {
      try {
        const result = await this.apiClient.get<DieticianProfileApiResponse>('/users/me/dietician-profile');

        return {
          success: true,
          data: result
        };
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'getProfile',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'getProfile',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'getProfile',
          error instanceof Error ? error.message : 'Failed to fetch dietician profile',
          httpError.status
        );
      }
    });
  }

  /**
   * Step 3: Configure dietician schedule
   *
   * @param userId - User ID for schedule configuration
   * @param data - Schedule configuration data
   * @returns Promise resolving to schedule response
   */
  async configureSchedule(
    userId: string,
    data: DieticianScheduleApiRequest
  ): Promise<ApiResponse<ScheduleResponse>> {
    return this.executeWithRetry(async () => {
      try {
        const result = await this.apiClient.put<{
          schedule_id: string;
          calendly_integrated: boolean;
        }>(`/dieticians/${userId}/schedule`, data);

        return {
          success: true,
          data: {
            scheduleId: result.schedule_id,
            calendlyIntegrated: result.calendly_integrated
          }
        };
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'configureSchedule',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'configureSchedule',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'configureSchedule',
          error instanceof Error ? error.message : 'Schedule configuration failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Utility: Upload profile photo
   *
   * @param file - Photo file to upload
   * @returns Promise resolving to photo URL
   */
  async uploadProfilePhoto(file: File): Promise<string> {
    return this.executeWithRetry(async () => {
      try {
        const formData = new FormData();
        formData.append('photo', file);

        const result = await this.apiClient.upload<{ photo_url: string }>('/upload/profile-photo', formData);

        return result.photo_url;
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'uploadProfilePhoto',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'uploadProfilePhoto',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        throw this.createError(
          'uploadProfilePhoto',
          error instanceof Error ? error.message : 'Photo upload failed',
          httpError.status
        );
      }
    });
  }

  /**
   * Search dietitians with optional filtering
   *
   * @param params Optional filtering parameters
   * @returns Promise resolving to array of dietitian profiles
   */
  async searchDietitians(params: GetDietitiansRequest = {}): Promise<DietitianProfile[]> {
    // Create a unique key based on search parameters for deduplication
    const searchKey = JSON.stringify({
      specialty: params.specialty,
      location: params.location,
      search: params.search,
      limit: params.limit,
      offset: params.offset
    });

    return this.executeWithDeduplication(`searchDietitians-${searchKey}`, async () => {
      return this.executeWithRetry(async () => {
        try {
          const queryParams = this.buildQueryParams(params);
          const endpoint = `${SEARCH_ENDPOINTS.DIETITIANS}${queryParams}`;

          // API returns array directly, not wrapped in response object
          const response = await this.apiClient.get<DietitianApiResponseProfile[]>(endpoint);

          return response.map(this.transformDietitianProfile);
        } catch (error) {
          // Handle timeout errors from API client
          if (error instanceof Error && error.name === 'AbortError') {
            throw this.createError(
              'searchDietitians',
              'Request timeout. Please check your connection and try again.',
              undefined,
              undefined,
              'TIMEOUT'
            );
          }

          // Handle other API client errors that might have error codes
          if (error && typeof error === 'object' && 'code' in error) {
            const apiError = error as {
              message?: string;
              status?: number;
              fieldErrors?: Record<string, string>;
              code?: string;
            };
            throw this.createError(
              'searchDietitians',
              apiError.message || 'An unexpected error occurred. Please try again.',
              apiError.status,
              apiError.fieldErrors,
              apiError.code
            );
          }

          const httpError = error as { status?: number };
          // Handle specific error cases
          if (httpError.status === 400) {
            throw this.createError(
              'searchDietitians',
              'Invalid search parameters. Please check your filters and try again.',
              400,
              undefined,
              'INVALID_PARAMETERS'
            );
          }
          if (httpError.status === 404) {
            throw this.createError(
              'searchDietitians',
              'No dietitians found matching your criteria.',
              404,
              undefined,
              'NO_DIETITIANS_FOUND'
            );
          }

          throw this.createError(
            'searchDietitians',
            error instanceof Error ? error.message : 'Search failed',
            httpError.status
          );
        }
      });
    });
  }

  /**
   * Get a specific dietitian by ID
   *
   * @param id Dietitian ID
   * @returns Promise resolving to dietitian profile
   */
  async getDietitianById(id: string): Promise<DietitianProfile> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.get<DietitianApiResponseProfile>(SEARCH_ENDPOINTS.DIETITIAN_BY_ID(id));

        return this.transformDietitianProfile(response);
      } catch (error) {
        // Handle timeout errors from API client
        if (error instanceof Error && error.name === 'AbortError') {
          throw this.createError(
            'getDietitianById',
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        // Handle other API client errors that might have error codes
        if (error && typeof error === 'object' && 'code' in error) {
          const apiError = error as {
            message?: string;
            status?: number;
            fieldErrors?: Record<string, string>;
            code?: string;
          };
          throw this.createError(
            'getDietitianById',
            apiError.message || 'An unexpected error occurred. Please try again.',
            apiError.status,
            apiError.fieldErrors,
            apiError.code
          );
        }

        const httpError = error as { status?: number };
        // Handle specific error cases
        if (httpError.status === 404) {
          throw this.createError(
            'getDietitianById',
            'Dietitian not found. Please check the ID and try again.',
            404,
            undefined,
            'DIETITIAN_NOT_FOUND'
          );
        }
        if (httpError.status === 400) {
          throw this.createError(
            'getDietitianById',
            'Invalid dietitian ID format.',
            400,
            undefined,
            'INVALID_ID'
          );
        }

        throw this.createError(
          'getDietitianById',
          error instanceof Error ? error.message : 'Failed to fetch dietitian',
          httpError.status
        );
      }
    });
  }

  /**
   * Search dietitians by name or specialty
   *
   * @param query Search query string
   * @returns Promise resolving to array of matching dietitian profiles
   */
  async searchDietitiansByQuery(query: string): Promise<DietitianProfile[]> {
    return this.searchDietitians({ search: query });
  }

  /**
   * Get dietitians by specialty
   *
   * @param specialty Health condition specialty to filter by
   * @returns Promise resolving to array of dietitians with the specified specialty
   */
  async getDietitiansBySpecialty(specialty: string): Promise<DietitianProfile[]> {
    return this.searchDietitians({ specialty });
  }

  /**
   * Get dietitians by location
   *
   * @param location Location to filter by
   * @returns Promise resolving to array of dietitians in the specified location
   */
  async getDietitiansByLocation(location: string): Promise<DietitianProfile[]> {
    return this.searchDietitians({ location });
  }

  /**
   * Build query parameters for dietitian requests
   *
   * @private
   * @param params Request parameters
   * @returns Query string
   */
  private buildQueryParams(params: GetDietitiansRequest): string {
    const searchParams = new URLSearchParams();

    if (params.specialty) searchParams.append('specialty', params.specialty);
    if (params.location) searchParams.append('location', params.location);
    if (params.search) searchParams.append('search', params.search);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Transform API response to frontend format
   *
   * @private
   * @param apiProfile API response profile
   * @returns Transformed dietitian profile
   */
  private transformDietitianProfile(apiProfile: DietitianApiResponseProfile): DietitianProfile {
    // Build location string from practice address fields
    const locationParts = [
      apiProfile.practice_city,
      apiProfile.practice_state,
      apiProfile.practice_country
    ].filter(Boolean);
    const location = locationParts.length > 0 ? locationParts.join(', ') : 'Location not specified';

    // Map areas of practice to health conditions
    const specialties = apiProfile.areas_of_practice.map(area => {
      // Map API enum values to frontend HealthCondition values
      switch (area) {
        case 'DIABETES_TYPE_1':
          return 'Diabetes - Type 1';
        case 'DIABETES_TYPE_2':
          return 'Diabetes - Type 2';
        case 'WEIGHT_MANAGEMENT':
          return 'Weight management';
        case 'HEALTHY_EATING':
          return 'Healthy eating advice';
        case 'HEART_DISEASE':
          return 'Heart disease (High cholesterol, blood pressure)';
        case 'SPORTS_NUTRITION':
          return 'Sports nutrition';
        default:
          // For unmapped values, convert snake_case to title case
          return area.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ');
      }
    }) as HealthCondition[];

    return {
      id: apiProfile.user_id,
      name: `${apiProfile.first_name} ${apiProfile.last_name}`,
      credentials: apiProfile.apd_id, // Using APD ID as credentials for now
      specialties,
      languagesSpoken: (apiProfile.languages_spoken || []) as LanguageCode[],
      location,
      rating: 0, // Not provided by API, defaulting to 0
      reviewCount: 0, // Not provided by API, defaulting to 0
      availability: 'accepting' as AvailabilityStatus, // Assuming accepting since they have scheduling_uri
      nextAvailable: 'Available for booking', // Generic message since specific availability not provided
      bio: `Registered Dietitian specialising in ${specialties.slice(0, 2).join(' and ')}${specialties.length > 2 ? ' and more' : ''}.`,
      avatar: apiProfile.photo || undefined,
    };
  }

  /**
   * Get domain name for error messages
   *
   * @protected
   * @returns Domain name
   */
  protected getDomainName(): string {
    return 'Dietician';
  }

  /**
   * Create domain-specific error instance
   *
   * @protected
   * @param operation - The operation that failed
   * @param message - Error message
   * @param status - HTTP status code
   * @param fieldErrors - Field-specific validation errors
   * @param code - Error code for programmatic handling
   * @returns DieticianApiError instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): DieticianApiError {
    const formattedMessage = `${operation}: ${message}`;
    return new DieticianApiError(formattedMessage, status, fieldErrors, code);
  }
}

/**
 * Default DieticianDomain instance
 */
export const dieticianDomain = new DieticianDomain();